import React, { useEffect, useState } from "react"
import StatusChip from "../../../../components/molecules/StatusChip.molecule"
import {
  ValueWithLabelContainer,
  ValueWithLabelTemplate,
} from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import { displayValueOrPlaceholder } from "../../../../utils/strings.utils"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import { getClientSprintSummaryBySprintId } from "../../../../services/clientReport.service"
import { getValidLuxonDate } from "../../../../utils/date.utils"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import ModalFormFinishClientSprint from "../../components/ModalFormFinishClientSprint/ModalFormFinishClientSprint.compound"
import {
  CLIENT_SPRINT_STATES,
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
  MODAL_FORM_CLOSE_SPRINT_MODE,
} from "../../../../consts/modules/clientReport.consts"
import { numberFormatter } from "../../../../utils/numbers.utils"
import { useSession } from "../../../../hooks/auth/useSession.hook"
import SprintApprovalTemplate from "../../components/SprintApprovalTemplate.molecule"
import useFinishClientSprintForm from "../../components/ModalFormFinishClientSprint/hooks/useFinishClientSprintForm.hook"
import {
  checkAllFields,
  fieldsHaveErrors,
} from "../../../../utils/validations.utils"
import useToast from "../../../../hooks/useToast.hook"
import {
  GENERIC_SAVE_ERROR_MESSAGE,
  GENERIC_SAVE_SUCCESS_MESSAGE,
} from "../../../../consts/modules/vacations.consts"

const ClientSprintSummary = ({
  refresh,
  triggerRefresh = () => {},
  sprintData = {},
}) => {
  const { setSuccessMessage, setErrorMessage } = useToast()
  const [loading, setLoading] = useState(true)
  const [sprintSummary, setSprintSummary] = useState({ ...sprintData })
  const [visibleCloseSprintModal, setVisibleCloseSprintModal] = useState(false)
  const [modalFormMode, setModalFormMode] = useState(
    MODAL_FORM_CLOSE_SPRINT_MODE.FINISH
  )

  const {
    loading: isModalFormLoading,
    setLoading: setModalFormLoading,
    formData,
    setFormData,
    validations,
    setValidations,
    resetForm,
    submitService,
    updateField,
    isFormValid,
  } = useFinishClientSprintForm({
    initialSprintData: sprintSummary,
    visible: visibleCloseSprintModal,
    mode: modalFormMode,
  })
  const { user } = useSession()
  useEffect(() => {
    fetchClientSprint()
  }, [sprintData, refresh])

  const fetchClientSprint = async () => {
    setLoading(true)
    try {
      if (!sprintData?.id) {
        setSprintSummary({
          ...sprintData,
          startDate: null,
          state: CLIENT_SPRINT_STATES.IN_PROGRESS,
        })
        return
      }
      const response = await getClientSprintSummaryBySprintId(sprintData.id)
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result || {}
      setModalFormMode(
        result?.state === CLIENT_SPRINT_STATES.CLOSED ?
          MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE
        : MODAL_FORM_CLOSE_SPRINT_MODE.FINISH
      )
      setSprintSummary({
        ...sprintData,
        ...result,
      })
    } catch (error) {
      console.error("Error fetching sprint summary:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    checkAllFields(validations, setValidations)
    if (fieldsHaveErrors(validations)) {
      return
    }

    setModalFormLoading(true)

    try {
      const response = await submitService()
      if (!response.success) {
        throw new Error(response.message)
      }
      resetForm()
      triggerRefresh()
      setSuccessMessage(GENERIC_SAVE_SUCCESS_MESSAGE)
    } catch (error) {
      setErrorMessage(GENERIC_SAVE_ERROR_MESSAGE)
    } finally {
      setModalFormLoading(false)
      setVisibleCloseSprintModal(false)
    }
  }

  return (
    <>
      <ClientReportCard
        key={sprintSummary}
        title={
          <div className="flex gap-2">
            <span>
              Sprint{" "}
              {sprintData.closeType === CLOSE_TYPES.TIMEOUT ?
                CLOSE_TYPE_LABELS.TIMEOUT.toLowerCase()
              : sprintData.sprintNumber}
            </span>
            <StatusChip
              status={sprintSummary?.state}
              tooltip={sprintSummary?.state === CLIENT_SPRINT_STATES.APPROVED}
              tooltipContent={
                <SprintApprovalTemplate
                  date={sprintSummary?.approvalDate}
                  approverFullName={`${
                    sprintSummary?.updatedByFirstName
                  } ${sprintSummary?.updatedByLastName}`}
                />
              }
              loading={loading}
            />
          </div>
        }
        headerButton={
          sprintSummary?.state !== CLIENT_SPRINT_STATES.APPROVED && (
            <PrimaryButton
              btnType="outlined"
              label={
                sprintSummary?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ?
                  "Cerrar sprint"
                : "Aprobar sprint"
              }
              icon={`pi pi-flag ${
                sprintSummary?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ?
                  "pi-flag"
                : "pi-check"
              }`}
              onClick={() => {
                if (
                  sprintSummary?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ||
                  sprintSummary?.state === CLIENT_SPRINT_STATES.CLOSED
                ) {
                  setVisibleCloseSprintModal(true)
                  setModalFormMode(
                    sprintSummary?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ?
                      MODAL_FORM_CLOSE_SPRINT_MODE.FINISH
                    : MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE
                  )
                }
              }}
              disabled={loading}
            />
          )
        }
        iconClassName="pi pi-paperclip text-3xl"
        className="flex-1 font-cairo flex-shrink-0 w-full"
      >
        <ValueWithLabelContainer>
          <ValueWithLabelTemplate
            label="Inicio"
            value={
              getValidLuxonDate(sprintSummary?.startDate, {})?.toFormat(
                "dd/MM/yyyy"
              ) || "-"
            }
            loading={loading}
          />
          <ValueWithLabelTemplate
            label="Cierre"
            value={
              getValidLuxonDate(sprintSummary?.endDate, {})?.toFormat(
                "dd/MM/yyyy"
              ) || "-"
            }
            loading={loading}
          />
          <ValueWithLabelTemplate
            label="Puntos realizados"
            value={
              <span className="flex align-items-center">
                <span>
                  {displayValueOrPlaceholder(
                    sprintSummary?.totalCompletedPointsSnapshot
                  )}
                </span>
                {user.isAdmin() &&
                  sprintSummary?.state !== CLIENT_SPRINT_STATES.IN_PROGRESS && (
                    <PrimaryButton
                      className="holiday-modal-trigger ml-2 vertical-align-middle flex-1 max-w-fit"
                      onClick={() => {
                        setVisibleCloseSprintModal(true)
                        setModalFormMode(
                          MODAL_FORM_CLOSE_SPRINT_MODE.FINISHED_EDIT
                        )
                      }}
                      rounded
                      icon="pi pi-pencil"
                    />
                  )}
              </span>
            }
            loading={loading}
          />
          <ValueWithLabelTemplate
            label="Horas registradas"
            value={
              (
                typeof sprintSummary?.totalRegisteredHoursSnapshot ===
                  "number" && sprintSummary?.totalRegisteredHoursSnapshot >= 0
              ) ?
                numberFormatter(sprintSummary?.totalRegisteredHoursSnapshot)
              : "-"
            }
            loading={loading}
          />
        </ValueWithLabelContainer>
      </ClientReportCard>
      <ModalFormFinishClientSprint
        visible={visibleCloseSprintModal}
        setVisible={setVisibleCloseSprintModal}
        sprintData={sprintSummary}
        mode={modalFormMode}
        loading={isModalFormLoading}
        formData={formData}
        setFormData={setFormData}
        validations={validations}
        setValidations={setValidations}
        resetForm={resetForm}
        updateField={updateField}
        isFormValid={isFormValid}
        handleSubmit={handleSubmit}
      />
    </>
  )
}

export default ClientSprintSummary
